// Library methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import { DiagonalArrowsIcon } from "src/assets/icons";

// Styles
import { CustomIconButtonDark } from "src/styles/mui/muiStylesHelper";

type DevicesHeaderType = {
  showButtons: boolean;
  isActive: string;
  setIsActive: React.Dispatch<React.SetStateAction<string>>;
  setExpandDevicesModal?: () => void;
};

const DevicesHeader = ({
  showButtons,
  isActive,
  setIsActive,
  setExpandDevicesModal = undefined,
}: DevicesHeaderType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const activeBorderColor = theme.palette.mode === "dark" ? "#FFF" : "#000";

  const headerButtons = useMemo(
    () =>
      showButtons
        ? [
            { type: "all", label: t("All") },
            { type: "linked", label: t("Linked") },
            { type: "unlinked", label: t("Unlinked") },
          ]
        : [],
    [showButtons, t]
  );

  return (
    <Box
      display="flex"
      justifyContent="flex-start"
      alignItems="center"
      gap="16px"
      width="100%"
    >
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box display="flex" alignItems="center" gap="16px">
          <Typography fontSize="16px" fontWeight={600}>
            {t("Devices")}
          </Typography>
          {headerButtons.map((headerButton) => (
            <Typography
              key={headerButton.type}
              sx={{
                fontWeight: isActive === headerButton.type ? 600 : 400,
                borderBottom:
                  isActive === headerButton.type
                    ? `1px solid ${activeBorderColor}`
                    : "1px solid transparent",
                fontSize: "14px",
                cursor: "pointer",
              }}
              onClick={() => setIsActive(headerButton.type)}
            >
              {headerButton.label}
            </Typography>
          ))}
        </Box>
        {setExpandDevicesModal && showButtons && (
          <Tooltip title={t("View All Devices")}>
            <IconButton
              aria-label="expand-devices"
              size="medium"
              sx={CustomIconButtonDark}
              onClick={setExpandDevicesModal}
            >
              <DiagonalArrowsIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default DevicesHeader;
